import { useQuery } from '@tanstack/vue-query'

import { beforeUnloadListener } from '~/lib/ui'
import { castToType } from '~/lib/utils'

export {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient
} from '@tanstack/vue-query'

export const useCountries = () => {
  const { axios } = useApi()
  const COUNTRIES_API_URL = `${window.location.protocol}//${window.location.host}/resources/countries.json`

  return useQuery({
    queryFn: async () => {
      const response = await axios.get<CountriesResponse>(COUNTRIES_API_URL)
      const countries = response.data
      countries.EP = 'Europe'
      countries.WO = 'Global'
      return countries
    },
    queryKey: ['countries']
  })
}

export const useInject = <T>(key: string) => {
  const provider = inject<T>(key)

  if (!provider) {
    const message = `${key} provider not found in ${getCurrentInstance()?.type.__name}`
    alert(message)
    throw new Error(message)
  }

  return provider
}

export const useUnsavedChanges = <F extends (...args: any) => any>(
  saver: F
) => {
  onBeforeUnmount(() => {
    removeEventListener('beforeunload', beforeUnloadListener, { capture: true })
  })

  return async (...args: Parameters<F>): Promise<ReturnType<F>> => {
    addEventListener('beforeunload', beforeUnloadListener, { capture: true })
    const result = await saver(...(args as any))
    if (result) {
      removeEventListener('beforeunload', beforeUnloadListener, {
        capture: true
      })
    }
    return result
  }
}

export const useRouteParam = <T = string>(param: string) => {
  const route = useRoute()
  return computed(() => route.params[param] as T)
}

export const useRouteQuery = <T>(query: string, fallbackValue: T) => {
  const route = useRoute()

  const value = computed(
    (): T => castToType(route.query[query] as string, fallbackValue)
  )
  const setValue = (v?: T) => {
    navigateTo({ query: { ...route.query, [query]: v as any } })
  }

  return [value, setValue] as const
}
